import { useCallback, useEffect, useState } from 'react';
import { KEYS } from '../storage/keys';
import { getString, setString } from '../storage/localStore';
import { BACKGROUND_THEMES, isDarkModeActive, type BackgroundTheme, type ThemeMode } from '../utils/themeStyles';

// Zelfde sleutels als de oude GameOn-versie (platte strings, geen JSON), zodat de gekozen
// weergave behouden blijft: 'gameon_theme' = light|dark|auto, 'gameon_bg_theme' = achtergrond.

/** Oude achtergrondnamen → template-id. De overige oude namen zijn al gelijk aan de template-id's. */
const LEGACY_BG_MAP: Record<string, BackgroundTheme> = {
  default: 'mesh_aurora',
  aurora: 'mesh_aurora',
};

export function parseThemeMode(raw: string | null): ThemeMode {
  return raw === 'light' || raw === 'dark' ? raw : 'auto';
}

export function parseBackground(raw: string | null, mode: ThemeMode): BackgroundTheme {
  // Oude app: zonder opgeslagen achtergrond hoort bij 'dark' de nachtachtergrond
  if (!raw) return mode === 'dark' ? 'midnight_velvet' : 'mesh_aurora';
  if (raw in LEGACY_BG_MAP) return LEGACY_BG_MAP[raw];
  return raw in BACKGROUND_THEMES ? (raw as BackgroundTheme) : 'mesh_aurora';
}

// Meerdere useTheme-instanties (app-shell + zijmenu) houden elkaar bij via dit event.
const SYNC_EVENT = 'gameon-theme-sync';
const notify = () => queueMicrotask(() => window.dispatchEvent(new Event(SYNC_EVENT)));

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(() => parseThemeMode(getString(KEYS.themeMode)));
  const [background, setBackground] = useState<BackgroundTheme>(() =>
    parseBackground(getString(KEYS.bgTheme), parseThemeMode(getString(KEYS.themeMode))),
  );
  const [dark, setDark] = useState(() => isDarkModeActive(mode) || background === 'midnight_velvet');

  useEffect(() => {
    const apply = () => {
      const isDark = isDarkModeActive(mode) || background === 'midnight_velvet';
      setDark(isDark);
      document.documentElement.classList.toggle('dark', isDark);
      document.documentElement.dataset.theme = background;
    };
    apply();
    setString(KEYS.themeMode, mode);
    setString(KEYS.bgTheme, background);
    if (mode !== 'auto') return;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    mq?.addEventListener('change', apply);
    const timer = window.setInterval(apply, 60_000); // vangt 20:00/07:00 op
    const onVisible = () => {
      if (document.visibilityState === 'visible') apply();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mq?.removeEventListener('change', apply);
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [mode, background]);

  useEffect(() => {
    const onSync = () => {
      const m = parseThemeMode(getString(KEYS.themeMode));
      setMode(m);
      setBackground(parseBackground(getString(KEYS.bgTheme), m));
    };
    window.addEventListener(SYNC_EVENT, onSync);
    return () => window.removeEventListener(SYNC_EVENT, onSync);
  }, []);

  const changeMode = useCallback((m: ThemeMode) => {
    setString(KEYS.themeMode, m);
    setMode(m);
    notify();
  }, []);
  const changeBackground = useCallback((b: BackgroundTheme) => {
    setString(KEYS.bgTheme, b);
    setBackground(b);
    notify();
  }, []);

  return { mode, setMode: changeMode, background, setBackground: changeBackground, dark };
}
